<template>
	<div class="grid-2-1">
		<div class="dg-card">
			<div class="dg-card-head">
				<h3><span v-text="$t('dashboard_gestao_desempenho_titulo')"></span></h3>
				<span class="dg-hint" :title="$t('dashboard_gestao_desempenho_tooltip')">
					<i class="fas fa-info-circle"></i>
				</span>
			</div>
			<div class="ranking-table-wrap">
				<table class="ranking-table">
					<thead>
						<tr>
							<th><span v-text="$t('dashboard_gestao_coluna_filial')"></span></th>
							<th><span v-text="$t('dashboard_gestao_coluna_cenarios')"></span></th>
							<th><span v-text="$t('dashboard_gestao_coluna_score')"></span></th>
							<th><span v-text="$t('dashboard_gestao_coluna_sem_edicao')"></span></th>
							<th><span v-text="$t('dashboard_gestao_coluna_swap_manual')"></span></th>
							<th><span v-text="$t('dashboard_gestao_coluna_lead_time')"></span></th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="r in ranking"
							:key="r.id"
							:class="{ selected: filial === r.id }"
							@click="onRowClick(r.id)"
						>
							<td class="rank-name">{{ r.nome }}</td>
							<td class="rank-n">{{ r.n }}</td>
							<td><span class="rank-pill" :style="{ background: heatColor(metricNorm('score', r.score), '22,163,74') }">{{ formatMetric(r.score) }}</span></td>
							<td><span class="rank-pill" :style="{ background: heatColor(metricNorm('touchless', r.touchless), '22,163,74') }">{{ formatMetric(r.touchless, 0, '%') }}</span></td>
							<td><span class="rank-pill" :style="{ background: heatColor(metricNorm('swap', r.swap), '220,38,38') }">{{ formatMetric(r.swap, 0, '%') }}</span></td>
							<td><span class="rank-pill" :style="{ background: heatColor(metricNorm('lead', r.lead, true), '47,159,224') }">{{ formatMetric(r.lead, 1, 'd') }}</span></td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		<div class="dg-card">
			<div class="dg-card-head">
				<h3><span v-text="$t('dashboard_gestao_custo_titulo')"></span></h3>
				<span class="dg-hint" :title="$t('dashboard_gestao_custo_tooltip')">
					<i class="fas fa-info-circle"></i>
				</span>
			</div>
			<div class="chart-wrap">
				<apexchart
					type="area"
					height="232"
					:options="chartOptions"
					:series="chartSeries"
				></apexchart>
			</div>
		</div>
	</div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import DashboardGestaoController from '@/views/controllers/DashboardGestaoController';

export default {
	name: 'DashboardGestaoRowDesempenho',
	components: { apexchart: VueApexCharts },
	props: {
		ranking: { type: Array, default: () => [] },
		filial: { type: String, default: 'geral' },
		cur: { type: Array, default: () => [] },
	},
	computed: {
		chartSeries() {
			return DashboardGestaoController.getCostChartData(this.cur).series;
		},
		chartOptions() {
			return DashboardGestaoController.getCostChartOptions(this.cur);
		},
		/** Faixas do próprio ranking, já que as métricas reais têm escalas distintas. */
		metricRanges() {
			return ['score', 'touchless', 'swap', 'lead'].reduce((ranges, key) => {
				const values = this.ranking.map(r => r[key]).filter(v => Number.isFinite(v));
				ranges[key] = values.length ? { min: Math.min(...values), max: Math.max(...values) } : null;
				return ranges;
			}, {});
		},
	},
	methods: {
		heatColor(norm, base) {
			return DashboardGestaoController.getHeatmapColor(norm, base);
		},
		metricNorm(key, value, invert = false) {
			const range = this.metricRanges[key];
			if (!Number.isFinite(value) || !range) return 0;
			if (range.max === range.min) return 0.5;
			const norm = (value - range.min) / (range.max - range.min);
			return invert ? 1 - norm : norm;
		},
		formatMetric(value, digits = 0, suffix = '') {
			if (!Number.isFinite(value)) return '–';
			return value.toFixed(digits) + suffix;
		},
		onRowClick(filialId) {
			const nextFilial = this.filial === filialId ? 'geral' : filialId;
			this.$emit('select-filial', nextFilial);
		},
	},
};
</script>

<style scoped lang="less">
@card: #ffffff;
@line: #e7e9f0;
@ink: #1a2036;
@muted: #6b7280;
@crimson: #d81f45;

.grid-2-1 {
	display: grid;
	grid-template-columns: 1.7fr 1fr;
	gap: 14px;
	margin-bottom: 14px;
}

@media (max-width: 1100px) {
	.grid-2-1 { grid-template-columns: 1fr; }
}

.dg-card {
	background: @card;
	border-radius: 14px;
	border: 1px solid @line;
	box-shadow: 0 1px 2px rgba(20, 29, 56, 0.04);
}

.dg-card-head {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 16px 18px 4px;

	h3 {
		margin: 0;
		font-size: 14.5px;
		font-weight: 700;
		color: @ink;
		letter-spacing: -0.2px;
	}

	.dg-hint {
		display: inline-flex;
		color: @muted;
		cursor: help;
	}
}

.chart-wrap {
	padding: 6px 10px 14px;
}

.ranking-table-wrap {
	padding: 0px 10px 14px;
	max-height: 280px;
	overflow: auto;
}

.ranking-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 12.5px;
	table-layout: fixed;

	thead tr { color: @muted; text-align: left; }

	th {
		padding: 8px 10px;
		font-weight: 600;
		text-align: center;
		position: sticky;
		top: 0;
		z-index: 1;
		background: @card;
	}

	tbody tr {
		cursor: pointer;
		border-top: 1px solid @line;
		&.selected { background: #fdf0f3; }
		&:hover { background: #f7f9fc; }
		&.selected:hover { background: #fde6ea; }
	}

	.rank-name {
		padding: 8px 10px;
		font-weight: 600;
		color: @ink;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	tr.selected .rank-name {
		font-weight: 800;
		color: @crimson;
	}

	.rank-n {
		padding: 8px 10px;
		text-align: center;
		color: @muted;
		font-weight: 600;
	}

	td { padding: 6px 8px; text-align: center; }

	.rank-pill {
		display: inline-block;
		min-width: 52px;
		padding: 4px 8px;
		border-radius: 7px;
		color: @ink;
		font-weight: 700;
	}
}
</style>
